import { type TimeSlot } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { useGetQualificationDisplayLabels } from "@src/appV2/Qualifications/api/useGetQualificationDisplayLabels";
import { usePendingPostBookingRequirementsForShifts } from "@src/appV2/redesign/Documents/api/wrappers/usePendingPostBookingRequirementsForShifts";
import { buildWorkplaceDocumentsPath } from "@src/appV2/redesign/Documents/paths";
import { RegularShiftBookedBottomSheet } from "@src/appV2/redesign/Shift/BookingConfirmationBottomSheet/RegularShiftBookedBottomSheet";
import { SHIFT_DISCOVERY_WORKPLACE_PROFILE_FULL_PATH } from "@src/appV2/redesign/ShiftDiscovery/paths";
import type { UseModalState } from "@src/appV2/redesign/system/ui-components";
import { calculateTotalPay } from "@src/appV2/redesign/utils/calculateShiftPay";
import { getShiftWorkDurationInHours } from "@src/appV2/redesign/utils/getShiftWorkDuration";
import { useGetFacilityBreakConfiguration } from "@src/appV2/Shifts/Shift/useGetFacilityBreakConfiguration";
import { useMemo } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { type ShiftInvite } from "../types";

interface ShiftInviteRegularShiftBookedBottomSheetContainerProps {
  modalState: UseModalState;
  shiftInvite: ShiftInvite;
}

export function ShiftInviteRegularShiftBookedBottomSheetContainer(
  props: ShiftInviteRegularShiftBookedBottomSheetContainerProps
) {
  const { modalState, shiftInvite } = props;

  const history = useHistory();

  const { mandatoryBreakDurationInMinutes } = useGetFacilityBreakConfiguration({
    workplaceId: shiftInvite.attributes.workplaceId,
  });

  const shiftWorkDurationInHours = getShiftWorkDurationInHours(
    {
      start: shiftInvite.attributes.shiftDetails.start,
      end: shiftInvite.attributes.shiftDetails.end,
    },
    shiftInvite.attributes.facility.requiresLunchBreak ?? false,
    mandatoryBreakDurationInMinutes
  );

  const hourlyPay = formatDollarsAsUsd(shiftInvite.attributes.shiftDetails.pay ?? 0);
  const { formattedPayAmount, payAmount } = calculateTotalPay({
    hourlyPayRate: shiftInvite.attributes.shiftDetails.pay ?? 0,
    hoursWorked: shiftWorkDurationInHours,
  });
  const { data: qualificationDisplayLabels, isLoading: isLoadingQualification } =
    useGetQualificationDisplayLabels([shiftInvite.attributes.shiftDetails.agentReq]);
  // eslint-disable-next-line prefer-destructuring
  const qualificationText = qualificationDisplayLabels[0];

  const shiftKey = shiftInvite.attributes.shiftId ?? shiftInvite.id;
  const qualification = shiftInvite.attributes.shiftDetails.agentReq;

  const shiftInputs = useMemo(
    () => [
      {
        shiftId: shiftKey,
        workplaceId: shiftInvite.attributes.workplaceId,
        workplaceType: shiftInvite.attributes.facility.type,
        msa: shiftInvite.attributes.facility.fullAddress?.metropolitanStatisticalArea,
        state: shiftInvite.attributes.facility.fullAddress?.state,
        region: shiftInvite.attributes.facility.fullAddress?.region,
        qualification,
      },
    ],
    [shiftKey, shiftInvite.attributes.workplaceId, shiftInvite.attributes.facility, qualification]
  );

  const { resultsByShiftId, isLoading: isLoadingPendingPostBookingRequirements } =
    usePendingPostBookingRequirementsForShifts(shiftInputs);
  const pendingPostBookingRequirementsCount = resultsByShiftId[shiftKey]?.count;

  useLogEffect(
    APP_V2_USER_EVENTS.POST_BOOKING_DOCUMENTS_SHEET_BANNER_SHOWN,
    {
      shiftId: shiftKey,
      workplaceId: shiftInvite.attributes.workplaceId,
      qualification,
      count: pendingPostBookingRequirementsCount,
    },
    {
      enabled:
        modalState.modalIsOpen &&
        isDefined(pendingPostBookingRequirementsCount) &&
        pendingPostBookingRequirementsCount > 0,
    }
  );

  const onCompleteDocumentsClick = () => {
    logEvent(APP_V2_USER_EVENTS.POST_BOOKING_DOCUMENTS_SHEET_CTA_CLICKED, {
      shiftId: shiftKey,
      workplaceId: shiftInvite.attributes.workplaceId,
      qualification,
      count: pendingPostBookingRequirementsCount,
    });

    modalState.closeModal();
    history.push(
      buildWorkplaceDocumentsPath({
        workplaceId: shiftInvite.attributes.workplaceId,
        qualification,
        shiftId: shiftKey,
        postBookingRequirementsFilter: "include-only",
      })
    );
  };

  return (
    <RegularShiftBookedBottomSheet
      modalState={modalState}
      shiftStartIso={shiftInvite.attributes.shiftDetails.start}
      shiftEndIso={shiftInvite.attributes.shiftDetails.end}
      shiftTimeSlot={shiftInvite.attributes.shiftDetails.name as TimeSlot}
      workplaceName={shiftInvite.attributes.facility.name}
      workplaceTimezone={shiftInvite.attributes.facility.tmz}
      shiftHourlyPay={hourlyPay}
      shiftFormattedTotalPay={formattedPayAmount}
      shiftTotalPay={payAmount}
      shiftBoost={undefined}
      requiresBreak={shiftInvite.attributes.facility.requiresLunchBreak}
      breakDurationInMinutes={mandatoryBreakDurationInMinutes}
      isLoadingQualification={isLoadingQualification}
      qualificationText={qualificationText}
      workplaceActionLabel="See workplace profile"
      isWorkplaceActionLoading={false}
      pendingPostBookingRequirementsCount={pendingPostBookingRequirementsCount}
      isLoadingPendingPostBookingRequirements={isLoadingPendingPostBookingRequirements}
      onCompleteDocumentsClick={onCompleteDocumentsClick}
      onWorkplaceActionClick={() => {
        logEvent(APP_V2_USER_EVENTS.WORKPLACE_PROFILE_OPENED, {
          workplaceId: shiftInvite.attributes.workplaceId,
          trigger: "Shift Invite Regular Shift Booked Bottom Sheet",
        });
        const path = generatePath(SHIFT_DISCOVERY_WORKPLACE_PROFILE_FULL_PATH, {
          workplaceId: shiftInvite.attributes.workplaceId,
        });
        modalState.closeModal();
        history.push(path);
      }}
    />
  );
}
